/**
 * Latin digits for everything catalogued — years, ids, centimetres, currency.
 * DESIGN.md sets those in IBM Plex Mono, which has no Persian numerals, and
 * mixing digit systems inside one inventory is worse than either choice alone.
 * This holds in both languages: the record reads the same either way.
 *
 * A record's *date* is the exception. It is prose, not an inventory value, so
 * it follows the reader's calendar and that calendar's own numerals.
 */
import { getLang, type Lang } from "./lang";

const decimal = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const money = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "";
  return decimal.format(Number(value));
}

export function formatMoney(value: number | null | undefined, code?: string): string {
  if (value === null || value === undefined) return "";
  return code ? `${money.format(value)} ${code}` : money.format(value);
}

/** `120 × 90 × 4` — always width × height; depth only tags along when both exist. */
export function formatDimensions(w: number | null, h: number | null, d: number | null): string {
  const finite = (n: number | null | undefined): number | null => {
    if (n === null || n === undefined) return null;
    const value = Number(n);
    return Number.isFinite(value) ? value : null;
  };
  const width = finite(w);
  const height = finite(h);
  const depth = finite(d);
  if (width === null && height === null) return "";
  const head = `${width === null ? "—" : formatNumber(width)} × ${
    height === null ? "—" : formatNumber(height)
  }`;
  return depth === null ? head : `${head} × ${formatNumber(depth)}`;
}

const MB = 1024 * 1024;
const KB = 1024;

export function formatBytes(bytes: number): string {
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  if (bytes >= KB) return `${Math.round(bytes / KB)} KB`;
  return `${bytes} B`;
}

/** Jalali in Persian, Gregorian in English — the calendar follows the reader. */
const dateFormats: Record<Lang, Intl.DateTimeFormat> = {
  fa: new Intl.DateTimeFormat("fa-IR", { year: "numeric", month: "long", day: "numeric" }),
  en: new Intl.DateTimeFormat("en-GB", { year: "numeric", month: "long", day: "numeric" }),
};

export function formatDate(iso: string, lang: Lang = getLang()): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : dateFormats[lang].format(date);
}

/**
 * Chooses the display font for a title. Fraunces italic is a Latin face; a
 * Persian title set in it would fall back mid-string and look broken.
 */
export function isLatinScript(text: string): boolean {
  const letters = text.replace(/[^\p{L}]/gu, "");
  if (!letters) return false;
  const latin = letters.match(/\p{Script=Latin}/gu)?.length ?? 0;
  return latin / letters.length > 0.6;
}

/** Direction of a catalogued name — follows the record's script, not the UI. */
export function recordDir(text: string): "ltr" | "rtl" {
  return isLatinScript(text) ? "ltr" : "rtl";
}
